import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBranches } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasAnyPermission, isAdminUser } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';

const BranchListPage = () => {
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalBranches, setTotalBranches] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const { user } = useCurrentUser();
    const navigate = useNavigate();
    const pageSize = 8;
    const currentPage = Math.min(page, totalPages);
    const startIndex = totalBranches === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endIndex = totalBranches === 0 ? 0 : Math.min(currentPage * pageSize, totalBranches);
    const pagedBranches = branches;

    const branchMeta = useMemo(
        () => ({
            total: totalBranches,
            totalPages,
        }),
        [totalBranches, totalPages]
    );

    useEffect(() => {
        if (user && isAdminUser(user)) {
            navigate('/settings', { replace: true });
        }
    }, [user, navigate]);

    if (user && isAdminUser(user)) {
        return null;
    }

    const handleSelectBranch = (branchId) => {
        localStorage.setItem('selectedBranchId', branchId);
        const canRegisterStock = hasAnyPermission(user, ['stock.manage', 'providers.manage']);
        const canUsePos = hasAnyPermission(user, ['pos.sell', 'pos.open', 'pos.close']);
        if (canRegisterStock && !canUsePos) {
            navigate(`/branches/${branchId}/stock`);
            return;
        }
        navigate(`/branches/${branchId}/purchases/new`);
    };

    const fetchBranches = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getBranches({ page, pageSize });
            const normalized = normalizePaginatedResponse(response);
            setBranches(normalized.data || []);
            setTotalBranches(normalized.total || 0);
            setTotalPages(normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize)));
        } catch (err) {
            console.error('Error fetching branches:', err);
            setError('No se pudieron cargar las sucursales.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, [page]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    if (loading) {
        return (
            <div className="p-6">
                <InlineLoading label="Cargando sucursales" />
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold">Selecciona una sucursal</h1>
                    <p className="text-sm text-gray-600">
                        Elige la sucursal para continuar con tus operaciones.
                    </p>
                </div>
                {error && (
                    <div className="mb-4 text-sm text-red-600">{error}</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pagedBranches.map((branch) => (
                        <button
                            key={branch.id}
                            type="button"
                            onClick={() => handleSelectBranch(branch.id)}
                            className="bg-white p-4 rounded shadow text-left hover:border-blue-300 border border-transparent"
                        >
                            <div className="text-lg font-medium text-gray-800">{branch.branchName}</div>
                            <div className="text-xs text-gray-500">Sucursal {branch.id}</div>
                        </button>
                    ))}
                </div>
                {branchMeta.total > pageSize && (
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-4">
                        <div>
                            Mostrando {startIndex}-{endIndex} de {branchMeta.total}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <span>
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
                {!branches.length && !error && (
                    <div className="bg-white rounded shadow p-6 text-sm text-gray-600">
                        No hay sucursales disponibles.
                    </div>
                )}
            </div>
        </div>
    );
};

export default BranchListPage;
